import { useQuery } from '@tanstack/react-query';
import type { ExchangeRate } from '@/features/catalog/types/exchange-rate.types';
import { apiFetch } from '@/lib/api';

/**
 * useBcvRate — Sprint 2b (kpis-de-compras) WU-4.
 *
 * Extracted from `ExchangeRateBadge.tsx:43-56` so both the TopBar
 * chip and the analytics-page currency toggle share the same
 * queryKey (`['exchange-rate','USD','VES']`) and therefore the same
 * TanStack cache entry. When a user has the TopBar visible AND opens
 * `/purchases/analytics`, the second mount hits the cache — no extra
 * network round trip.
 *
 * The wire shape (`ExchangeRate.rate`) is a `z.string()` (PG numeric
 * serialized as text to avoid JS float loss). Callers MUST parse it
 * to a number before doing arithmetic — never `JSON.parse(rate)`,
 * never pass the raw string into arithmetic.
 *
 * Defaults mirror the badge: 30-minute staleTime (BCV publishes once
 * a day), no auto-refetch on focus/reconnect, retry once. The
 * `refetch()` button in the badge still works because it calls the
 * same `refetch` returned by `useQuery`.
 */
export function useBcvRate() {
  return useQuery({
    queryKey: ['exchange-rate', 'USD', 'VES'] as const,
    queryFn: () =>
      apiFetch<ExchangeRate>('/exchange-rates/latest?from=USD&to=VES', {
        skipAuthRedirect: true,
      }),
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: 1,
  });
}
